import React, { useEffect, useRef } from "react";
// Import GSAP and ScrollTrigger
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const MissionSection = () => {
  // 1. Create a ref to hold the section element for GSAP context
  const sectionRef = useRef(null);

  useEffect(() => {
    // 2. Create a GSAP context to manage and revert animations easily
    const context = gsap.context(() => {
      // ----------------------------------------------------
      // A. Text and Card Reveal Animation (Merise.ae style)
      // ----------------------------------------------------
      // Select all elements that should animate in
      const animatedElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".mission-animated-content")
      );

      animatedElements.forEach((element) => {
        // Set Initial State: Hidden (opacity: 0), slightly down (y: 25), and clipped from the bottom
        gsap.set(element, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            // Use the element itself as the trigger
            trigger: element,
            start: "top 90%", // Start animation earlier when element comes into view
            end: "center 70%", // End animation soon after the element is visible
            scrub: 1, // Smooth link to scroll position
            // markers: true, // Uncomment for debugging scroll positions
          },
        });

        // Define the Target State: Visible (opacity: 1), original position (y: 0), and fully unclipped
        tl.to(element, {
          y: 0, // Final state: Original position (move up)
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)", // Fully visible
          duration: 1,
          ease: "power2.out", // Smooth movement
          // Note: Staggering is not used here as each card/block is animated separately by its own ScrollTrigger
        });
      });

      // ----------------------------------------------------
      // B. Image Slide-In Animation (from the left)
      // ----------------------------------------------------
      const imageElement = sectionRef.current.querySelector(".mission-image");

      if (imageElement) {
        // Set Initial State: Off-screen to the left (x: -50), slight rotation for visual interest
        gsap.set(imageElement, {
          x: -50,
          opacity: 0,
          rotation: -2,
        });

        gsap.to(imageElement, {
          x: 0, // Slide to final position
          opacity: 1,
          rotation: 0, // Straighten out
          duration: 1.5,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current, // Trigger when the whole section comes into view
            start: "top 80%",
            end: "top 40%",
            scrub: 1.5, // Slower scrub for a smoother image movement
            // markers: true,
          },
        });
      }
    }, sectionRef); // Pass the ref to the context

    // Cleanup function to revert all ScrollTriggers and animations when component unmounts
    return () => context.revert();
  }, []);

  return (
    <section
      // Attach the ref here
      ref={sectionRef}
      className="w-full mt-[50px] sm:mt-[75px] xl:mt-[100px]"
    >
      <div className="w-full max-w-[1620px] mx-auto px-4 sm:px-6 xl:px-8">
        <div className="flex flex-col xl:flex-row items-center gap-8 xl:gap-[66px]">
          {/* Left Image */}
          <div className="w-full xl:w-[56%]">
            <img
              src="/assets/missionImg.png"
              alt="Organic Farming Image"
              // Add the class for the image animation
              className="mission-image w-full h-auto object-cover rounded-xl"
            />
          </div>

          {/* Right Content */}
          <div className="w-full xl:w-[42%] space-y-4 xl:space-y-[22px]">
            {/* Mission Card */}
            {/* Add mission-animated-content for the whole card to reveal */}
            <div className="mission-animated-content bg-secondary-background rounded-xl xl:rounded-xl p-4 sm:p-6 xl:p-[30px]">
              <div className="space-y-2 xl:space-y-[4px]">
                <h3 className="text-[26px] mb-[20px] sm:text-[32px] xl:text-md font-bold leading-[36px] sm:leading-[48px] xl:leading-loose text-white text-left font-['Poppins']">
                  Our Mission
                </h3>
                <p className="text-xs xl:text-xs font-light leading-tight text-white text-justify mb-1 xl:mb-[6px]">
                  <span className="font-bold">UTPANN</span> is committed to
                  promoting natural farming practices, reducing dependence on
                  chemical fertilizers, and building a future where agriculture
                  thrives in harmony with nature—ensuring{" "}
                  <span className="font-bold">
                    healthy soil, healthy food, and a healthy nation.
                  </span>
                </p>
              </div>
            </div>

            {/* Strategy Card */}
            {/* Add mission-animated-content for the whole card to reveal */}
            <div className="mission-animated-content bg-secondary-background rounded-xl xl:rounded-xl p-4 sm:p-6 xl:p-[30px]">
              <div className="space-y-3 xl:space-y-[4px]">
                <h3 className="text-[26px]  mb-[20px]  sm:text-[32px] xl:text-md font-bold leading-[36px] sm:leading-[48px] xl:leading-loose text-white text-left font-['Poppins']">
                  Our Strategy
                </h3>
                <p className="text-xs xl:text-xs font-light leading-tight text-white text-justify">
                  To drive the growth of{" "}
                  <span className="font-bold">organic agriculture</span> through
                  continuous innovation, farmer awareness, and strong
                  distribution networks. UTPANN focuses on delivering{" "}
                  <span className="font-bold">
                    high-quality organic fertilizers
                  </span>
                  , expanding infrastructure, and building partnerships that
                  promote{" "}
                  <span className="font-bold">
                    soil health, sustainability, and long-term agricultural
                    prosperity.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionSection;
